import { useState, useEffect, useCallback } from "react"
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { Title } from "components/Common/Title"
import analyticsService from "services/analyticsService"
import type { IAnalyticsFailedExam } from "types/analyticsTypes"

const PERIOD_OPTIONS = [
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 365 days", value: 365 },
  { label: "All time", value: 0 },
]

type Props = {
  orgId: string | null
}

export function ExamFailedSection({ orgId }: Props) {
  const [period, setPeriod] = useState<number>(90)
  const [data, setData] = useState<IAnalyticsFailedExam[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getExamsFailed(orgId || undefined, p)
      setData(result)
    } catch {
      setError("Failed to load failed exam data.")
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    load(period)
  }, [load, period])

  const handlePeriodChange = (event: SelectChangeEvent<number>) => {
    const newPeriod = Number(event.target.value)
    setPeriod(newPeriod)
  }

  const retryLoad = () => load(period)

  const showOrgColumn = !orgId

  return (
    <Paper sx={{ p: 3 }}>
      <Title>FAILED EXAMS</Title>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Period</InputLabel>
          <Select<number> value={period} label="Period" onChange={handlePeriodChange} disabled={loading}>
            {PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box>
          <Skeleton variant="rectangular" width="100%" height={36} sx={{ mb: 1 }} />
          {Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((key) => (
            <Skeleton key={key} variant="rectangular" width="100%" height={32} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={retryLoad}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        data.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No failed exam records found.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 500, overflow: "auto" }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
                    {showOrgColumn && <TableCell sx={{ fontWeight: "bold" }}>Organisation</TableCell>}
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Crane</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Tool</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Fail Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={`${row.lessonId}-${row.orgId}-${row.date}-${row.crane}-${row.tool}-${row.failReason}`}>
                      <TableCell>{row.lessonName ?? `Unknown (${row.lessonId})`}</TableCell>
                      {showOrgColumn && <TableCell>{row.orgName}</TableCell>}
                      <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                      <TableCell>{row.crane}</TableCell>
                      <TableCell>{row.tool}</TableCell>
                      <TableCell>{row.failReason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
    </Paper>
  )
}
